import type { SupabaseClient } from "@supabase/supabase-js";

// PostgREST membatasi jumlah baris per response (Supabase default `max-rows` =
// 1000) — BERLAKU JUGA untuk resource ter-embed per induk. Produk bisa punya
// RIBUAN kombinasi varian (mis. Warna 16 × Ukuran 16 × Motif 16 = 4096), jadi
// SATU query (`product_variants(*)` ter-embed ATAU `.select().eq(product_id)`
// top-level) hanya mengembalikan 1000 varian pertama. Akibatnya di storefront
// kombinasi selebihnya HILANG (tak muncul di selektor → tak bisa dipesan), dan
// di ProductForm edit varian yang tak termuat dianggap "dibuang" saat Simpan →
// TERHAPUS. Ambil SEMUA varian secara terpaginasi menghindari kedua bug itu.
export const PGREST_MAX_ROWS = 1000;

// Berapa banyak halaman diambil PARALEL sekaligus. Produk sangat besar (mis.
// 10.000 kombinasi = 10 halaman) kalau diambil SEKUENSIAL menumpuk latensi
// per-request dan bisa menembus timeout pemanggil (halaman detail produk
// membungkus fetch ini dalam batas waktu). Mengambil beberapa halaman serentak
// memangkasnya jadi sedikit "gelombang". 5 = seimbang antara kecepatan &
// beban ke PostgREST.
export const VARIANT_PAGE_CONCURRENCY = 5;

async function fetchVariantPage<T>(
  db: SupabaseClient,
  productId: string,
  columns: string,
  from: number,
  to: number,
  withCount: boolean,
  or: string | undefined
): Promise<{ rows: T[]; count: number | null }> {
  let query = db
    .from("product_variants")
    .select(columns, withCount ? { count: "exact" } : undefined)
    .eq("product_id", productId);
  if (or) query = query.or(or);
  const { data, error, count } = await query
    .order("sort_order", { ascending: true })
    .order("id", { ascending: true })
    .range(from, to);
  if (error) throw new Error(error.message);
  return { rows: (data ?? []) as T[], count: count ?? null };
}

/**
 * Ambil SEMUA baris `product_variants` sebuah produk secara terpaginasi
 * (menembus batas `max-rows` PostgREST). Urut stabil `(sort_order, id)` agar
 * paginasi konsisten. `columns` = daftar kolom PostgREST (mis. `"*"` atau
 * `"id, name, sku, option_ids, ..."`). Lempar pada error DB (pemanggil yang
 * memutuskan fallback), kembalikan [] bila kosong.
 *
 * Strategi: halaman pertama sekaligus meminta TOTAL (`count: 'exact'`, murah
 * karena ter-index `product_id`) sehingga offset semua halaman sisa diketahui &
 * bisa diambil PARALEL ber-konkurensi terbatas — jauh lebih cepat daripada
 * sekuensial untuk produk ribuan varian, sambil mempertahankan urutan hasil.
 * Bila count tak tersedia (konfigurasi tak terduga), jatuh ke loop sekuensial
 * "sampai halaman pendek" (perilaku lama, tetap benar).
 *
 * `opts.or` = saringan `or` PostgREST yang dikerjakan SERVER di setiap halaman
 * (termasuk hitungan `count`, jadi offset halaman sisa tetap konsisten). Untuk
 * produk ribuan kombinasi yang sebagian besar tak relevan bagi pemanggil —
 * /admin/stok tak perlu mengunduh 21 rb kombinasi nonaktif berstok 0 hanya
 * untuk dibuang di klien.
 */
export async function fetchAllProductVariants<T = Record<string, unknown>>(
  db: SupabaseClient,
  productId: string,
  columns: string,
  pageSize: number = PGREST_MAX_ROWS,
  opts?: { or?: string }
): Promise<T[]> {
  const step = Math.max(1, Math.floor(pageSize));
  const or = opts?.or;

  const first = await fetchVariantPage<T>(db, productId, columns, 0, step - 1, true, or);
  // Halaman pertama tak penuh → itu SEMUA varian (produk kecil, mayoritas kasus).
  if (first.rows.length < step) return first.rows;

  const total = first.count;

  // Jalur PARALEL: total diketahui → daftar offset halaman sisa lalu ambil
  // ber-konkurensi. Urutan dijaga lewat indeks slot (bukan urutan selesai).
  if (total != null) {
    const starts: number[] = [];
    for (let from = step; from < total; from += step) starts.push(from);
    if (starts.length === 0) return first.rows;

    const pages: T[][] = new Array(starts.length);
    let cursor = 0;
    const worker = async () => {
      for (let idx = cursor++; idx < starts.length; idx = cursor++) {
        const from = starts[idx];
        const { rows } = await fetchVariantPage<T>(
          db,
          productId,
          columns,
          from,
          from + step - 1,
          false,
          or
        );
        pages[idx] = rows;
      }
    };
    const workerCount = Math.min(VARIANT_PAGE_CONCURRENCY, starts.length);
    await Promise.all(Array.from({ length: workerCount }, worker));
    return first.rows.concat(...pages);
  }

  // Jalur FALLBACK sekuensial (count tak tersedia): sampai halaman pendek.
  const all: T[] = [...first.rows];
  for (let from = step; ; from += step) {
    const { rows } = await fetchVariantPage<T>(
      db,
      productId,
      columns,
      from,
      from + step - 1,
      false,
      or
    );
    all.push(...rows);
    if (rows.length < step) break;
  }
  return all;
}

/**
 * Versi umum `hydrateCappedVariants` untuk embed SELAIN product_variants (mis.
 * `orders(order_items(...))` per produk): untuk tiap baris yang array embed
 * `embedKey`-nya menyentuh cap (`>= PGREST_MAX_ROWS`), panggil `refetch(row)`
 * (biasanya `fetchAllRows` yang difilter ke induk itu) dan timpa arraynya.
 * Kasus umum (di bawah cap) = nol query tambahan. Memutasi `rows` di tempat.
 */
export async function hydrateCappedEmbed<T extends Record<string, any>>(
  rows: T[],
  embedKey: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetch: (row: T) => Promise<any[]>
): Promise<T[]> {
  const capped = rows.filter(
    (r) => Array.isArray(r[embedKey]) && r[embedKey].length >= PGREST_MAX_ROWS
  );
  if (capped.length === 0) return rows;
  await Promise.all(
    capped.map(async (r) => {
      (r as Record<string, unknown>)[embedKey] = await refetch(r);
    })
  );
  return rows;
}

/**
 * Paginator generik: jalankan `page(from,to)` berulang (`.range()`) sampai
 * halaman lebih pendek dari `pageSize`, akumulasi semua baris. Untuk query
 * TOP-LEVEL yang bisa melewati `max-rows` PostgREST (1000) secara total (mis.
 * `product_variants`/`order_items`/`orders` lintas produk). Pemanggil WAJIB
 * memberi urutan total & stabil (mis. `.order("id")`) agar paginasi tak
 * melompati/menggandakan baris. Melempar pada error DB.
 */
export async function fetchAllRows<T>(
  // `data: any[]` sengaja longgar: string `.select(...)` kompleks membuat
  // supabase-js menyimpulkan tipe baris `GenericStringError[]` yang tak cocok
  // dengan T konkret — pemanggil menentukan T sebagai tipe hasil.
  page: (
    from: number,
    to: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => PromiseLike<{ data: any[] | null; error: { message: string } | null }>,
  pageSize: number = PGREST_MAX_ROWS
): Promise<T[]> {
  const step = Math.max(1, Math.floor(pageSize));
  const all: T[] = [];
  for (let from = 0; ; from += step) {
    const { data, error } = await page(from, from + step - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as T[];
    all.push(...rows);
    if (rows.length < step) break;
  }
  return all;
}

/**
 * "Refetch bila kena cap": untuk query yang meng-EMBED `product_variants(...)`
 * per banyak induk sekaligus (mis. board/stok/daftar produk), array embed tiap
 * induk dipotong PostgREST pada `max-rows` (1000). Fungsi ini menerima baris
 * hasil query lalu — HANYA untuk induk yang arraynya menyentuh cap
 * (`length >= PGREST_MAX_ROWS`, indikasi kemungkinan terpotong) — mengambil
 * ULANG SEMUA varian induk itu secara terpaginasi dan menimpanya. Kasus umum
 * (semua produk <1000 varian) = NOL query tambahan, jadi murah.
 *
 * `columns` HARUS sama dengan kolom varian di embed asli agar bentuk baris
 * konsisten dengan pemetaan pemanggil. `filter` opsional (mis. stok>0) meniru
 * embed ber-`!inner`/filter. `serverOr` opsional = saringan `or` yang SAMA
 * dengan embed asli, dikerjakan server saat mengambil ulang (lihat
 * `fetchAllProductVariants`) — tanpa ini produk 22 rb kombinasi diunduh
 * seluruhnya walau embed-nya sudah disaring. Memutasi `rows` di tempat (juga
 * mengembalikannya).
 */
export async function hydrateCappedVariants<T extends Record<string, any>>(
  db: SupabaseClient,
  rows: T[],
  columns: string,
  opts?: {
    idKey?: string;
    variantsKey?: string;
    filter?: (v: Record<string, any>) => boolean;
    serverOr?: string;
  }
): Promise<T[]> {
  const idKey = opts?.idKey ?? "id";
  const vKey = opts?.variantsKey ?? "product_variants";
  const capped = rows.filter(
    (r) => Array.isArray(r[vKey]) && r[vKey].length >= PGREST_MAX_ROWS
  );
  if (capped.length === 0) return rows;

  await Promise.all(
    capped.map(async (r) => {
      let all = await fetchAllProductVariants<Record<string, any>>(
        db,
        r[idKey] as string,
        columns,
        PGREST_MAX_ROWS,
        { or: opts?.serverOr }
      );
      if (opts?.filter) all = all.filter(opts.filter);
      (r as Record<string, unknown>)[vKey] = all;
    })
  );
  return rows;
}

/**
 * Daftar nilai filter `in` PostgREST — untuk `.filter(kolom, "in", ...)` —
 * yang aman untuk teks APA PUN. `.in()` bawaan postgrest-js hanya mengutip
 * nilai ber-`,()` dan tak pernah meng-escape `"` / `\`, jadi nama varian
 * ber-kutip (mis. `Size 5"`) merusak sintaks filternya. Di sini SETIAP nilai
 * dikutip, lalu `\` dan `"` di dalamnya di-escape backslash (aturan reserved
 * characters PostgREST — diuji ke PROD 2026-10-07: `"…/ \L"` dibaca `…/ L`).
 */
export function postgrestInList(values: readonly string[]): string {
  return `(${values.map((v) => `"${v.replace(/[\\"]/g, "\\$&")}"`).join(",")})`;
}

/**
 * Batas nama — dan batas produk — per request `fetchVariantsByName`. Nama
 * varian terpanjang di PROD ±90 karakter setelah di-encode, UUID produk ±40:
 * 40 nama + 40 produk ≈ 5 kB, aman di bawah batas baris request proxy (±8 kB).
 */
export const VARIANT_NAME_CHUNK = 40;

/**
 * Ambil baris `product_variants` untuk pasangan (produk, NAMA varian) yang
 * diminta — varian yang dibutuhkan pemanggil tanpa memuat seluruh varian
 * produk (status kartu tab Produksi hanya menilai varian yang DIPESAN, lihat
 * `fetchProductionCardFlagsFromSupabase`). Dicocokkan lewat NAMA, bukan
 * `variant_id`, karena begitulah Detail Produksi mencocokkan item ke varian
 * (`aggregateProductionDetail`): dua halaman harus menurunkan angka yang sama.
 * `(product_id, name)` unik di DB (`product_variants_product_id_name_key`),
 * jadi satu pasangan = paling banyak satu baris.
 *
 * Semua produk digabung ke sesedikit mungkin request (≤ `chunkSize` nama DAN
 * ≤ `chunkSize` produk per request, masing-masing terpaginasi — keduanya ikut
 * memanjangkan URL). Filter `name in (…)` lintas produk ikut
 * menjaring varian SENAMA milik produk lain — baris itu dibuang di sini, jadi
 * hasilnya persis pasangan yang diminta. `columns` WAJIB memuat `product_id`
 * dan `name`. Peta kosong = nol request. Melempar pada error DB.
 */
export async function fetchVariantsByName<T extends { product_id: string; name: string }>(
  db: SupabaseClient,
  namesByProduct: ReadonlyMap<string, readonly string[]>,
  columns: string,
  chunkSize: number = VARIANT_NAME_CHUNK
): Promise<Map<string, T[]>> {
  const step = Math.max(1, Math.floor(chunkSize));
  const chunks: { productIds: Set<string>; names: Set<string> }[] = [];
  let cur = { productIds: new Set<string>(), names: new Set<string>() };
  for (const [productId, names] of namesByProduct) {
    for (const name of names) {
      const full =
        (!cur.names.has(name) && cur.names.size >= step) ||
        (!cur.productIds.has(productId) && cur.productIds.size >= step);
      if (full) {
        chunks.push(cur);
        cur = { productIds: new Set<string>(), names: new Set<string>() };
      }
      cur.productIds.add(productId);
      cur.names.add(name);
    }
  }
  if (cur.names.size > 0) chunks.push(cur);

  const pages = await Promise.all(
    chunks.map((c) =>
      fetchAllRows<T>((from, to) =>
        db
          .from("product_variants")
          .select(columns)
          .in("product_id", [...c.productIds])
          .filter("name", "in", postgrestInList([...c.names]))
          .order("id", { ascending: true })
          .range(from, to)
      )
    )
  );

  // Nama satu produk yang terbelah ke dua chunk bisa terjaring dua kali bila
  // produknya ikut di kedua chunk — kunci (produk, nama) menyaring duplikatnya.
  const out = new Map<string, T[]>();
  const taken = new Set<string>();
  for (const row of pages.flat()) {
    if (!namesByProduct.get(row.product_id)?.includes(row.name)) continue;
    const key = `${row.product_id}\u0000${row.name}`;
    if (taken.has(key)) continue;
    taken.add(key);
    const list = out.get(row.product_id);
    if (list) list.push(row);
    else out.set(row.product_id, [row]);
  }
  return out;
}
